import { Popover } from '@headlessui/react';
import { AdjustmentsHorizontalIcon, Bars3Icon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import {
    DndContext,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { DndContextProps } from '@dnd-kit/core';
import type { DpColumnId, DpColumnProfile } from '../../vacationDpTable.definitions';
import { LOCKED_COLUMNS, getColumnLabel } from '../../vacationDpTable.definitions';
import { COLUMN_MENU_LABELS } from './vacationDpColumnMenu.definitions';
import type { VacationDpColumnMenuProps } from './vacationDpColumnMenu.types';

function ColumnMenuItem({
    id,
    label,
    visible,
    onToggle,
    profile,
}: {
    id: DpColumnId;
    label: string;
    visible: boolean;
    onToggle: (id: DpColumnId) => void;
    profile: DpColumnProfile;
}) {
    const { attributes, listeners, isDragging, transform, setNodeRef } = useSortable({
        id,
        disabled: LOCKED_COLUMNS.includes(id),
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition: 'all 150ms ease',
    };

    const isLocked = LOCKED_COLUMNS.includes(id);

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={clsx(
                'flex items-center gap-2 px-3 py-2 rounded',
                isDragging && 'opacity-50 z-10',
                isLocked && 'bg-raro-gray-50'
            )}
        >
            {!isLocked && (
                <button
                    {...attributes}
                    {...listeners}
                    type="button"
                    aria-label={COLUMN_MENU_LABELS.dragHandle}
                    className="flex-shrink-0 text-raro-gray-400 hover:text-raro-gray-600 transition-colors"
                >
                    <Bars3Icon className="h-4 w-4" />
                </button>
            )}
            {isLocked && <div className="w-4 flex-shrink-0" />}

            <label className="flex items-center gap-2 flex-1 cursor-pointer">
                <input
                    type="checkbox"
                    checked={visible}
                    onChange={() => onToggle(id)}
                    disabled={isLocked}
                    className={clsx(
                        'rounded border-raro-gray-300',
                        isLocked && 'cursor-not-allowed opacity-50'
                    )}
                />
                <span
                    className={clsx(
                        'text-sm font-montserrat',
                        isLocked && 'text-raro-gray-500',
                        !isLocked && 'text-raro-gray-900'
                    )}
                >
                    {label}
                </span>
            </label>
        </div>
    );
}

export function VacationDpColumnMenu({
    columnOrder,
    columnVisibility,
    onToggleColumn,
    onMoveColumn,
    onResetToDefault,
    isCustomized,
    profile = 'general',
}: VacationDpColumnMenuProps & { profile?: DpColumnProfile }) {

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd: DndContextProps['onDragEnd'] = (event) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            onMoveColumn(active.id as DpColumnId, over.id as DpColumnId);
        }
    };

    return (
        <Popover className="relative">
            <Popover.Button
                className={clsx(
                    'inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm font-montserrat',
                    'border border-raro-gray-300 bg-white text-raro-gray-900',
                    'hover:bg-raro-gray-50 transition-colors'
                )}
                aria-label={COLUMN_MENU_LABELS.buttonAriaLabel}
            >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
                <span>{COLUMN_MENU_LABELS.title}</span>
                {isCustomized && (
                    <div className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-hidden="true" />
                )}
            </Popover.Button>

            <Popover.Panel className="absolute right-0 z-10 mt-2 w-64 rounded-lg border border-raro-gray-200 bg-white shadow-lg">
                <div className="p-3 space-y-2 max-h-64 overflow-y-auto">
                    <h3 className="text-xs font-semibold text-raro-gray-500 uppercase px-3 py-2">
                        {COLUMN_MENU_LABELS.title}
                    </h3>

                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={columnOrder.filter(id => !LOCKED_COLUMNS.includes(id))}
                            strategy={verticalListSortingStrategy}
                        >
                            {columnOrder.map((id) => (
                                <ColumnMenuItem
                                    key={id}
                                    id={id}
                                    label={getColumnLabel(profile, id)}
                                    visible={columnVisibility[id]}
                                    onToggle={onToggleColumn}
                                    profile={profile}
                                />
                            ))}
                        </SortableContext>
                    </DndContext>
                </div>

                <div className="border-t border-raro-gray-200 px-3 py-2">
                    <button
                        onClick={() => {
                            onResetToDefault();
                        }}
                        disabled={!isCustomized}
                        className={clsx(
                            'w-full px-3 py-2 text-xs font-montserrat rounded transition-colors',
                            isCustomized
                                ? 'text-primary-600 hover:bg-primary-50'
                                : 'text-raro-gray-400 cursor-not-allowed'
                        )}
                    >
                        {COLUMN_MENU_LABELS.resetToDefault}
                    </button>
                </div>
            </Popover.Panel>
        </Popover>
    );
}

export default VacationDpColumnMenu;
